/**
 * Freshness rules for a trail's grooming state.
 *
 * Grooming is reported as a time-decaying event, not a sticky boolean:
 * a chip surfaces "Groomed 3h ago" while fresh, fades as it ages, and
 * stops rendering past the cutoff. The underlying `grooming` value on
 * the trail row is left alone — only the user-facing chip decays.
 *
 *   < 12h  fresh   sky-blue chip
 *   12-24h aging   grey chip
 *   > 24h  stale   no chip
 *
 * The "Partial" kind uses the same windows; only the kind label changes.
 */

const FRESH_WINDOW_HOURS = 12;
const SHOW_WINDOW_HOURS = 24;

function hoursSince(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return null;
    return (Date.now() - t) / 3600000;
}

function kindNoun(grooming) {
    return grooming === 'partial' ? 'Partial groom' : 'Groomed';
}

function howLongAgo(hours) {
    if (hours < 1) return 'just now';
    const h = Math.round(hours);
    if (h === 1) return '1h ago';
    return `${h}h ago`;
}

/**
 * Returns { label, fresh } when the chip should render, null otherwise.
 * `label` is the chip text ("Groomed 3h ago"); `fresh` drives colour.
 */
export function groomingFreshness(groomedAt, grooming) {
    if (!grooming || grooming === 'none') return null;
    const hrs = hoursSince(groomedAt);
    if (hrs === null || hrs < 0 || hrs > SHOW_WINDOW_HOURS) return null;
    return {
        label: `${kindNoun(grooming)} ${howLongAgo(hrs)}`,
        fresh: hrs < FRESH_WINDOW_HOURS,
    };
}

/**
 * Long-form "last groomed" line for the editor — shown regardless of
 * the SHOW window so the crew can see the stored timestamp even when
 * it's gone stale. Returns null when grooming is 'none' / no timestamp.
 */
export function lastGroomedLabel(groomedAt, grooming) {
    if (!grooming || grooming === 'none' || !groomedAt) return null;
    const hrs = hoursSince(groomedAt);
    if (hrs === null || hrs < 0) return null;
    if (hrs <= SHOW_WINDOW_HOURS) {
        return `Last ${kindNoun(grooming).toLowerCase()}: ${howLongAgo(hrs)}`;
    }
    const days = Math.round(hrs / 24);
    const ago = days <= 1 ? '1 day ago' : `${days} days ago`;
    return `Last ${kindNoun(grooming).toLowerCase()}: ${ago} (stale)`;
}
